<template>
  <div v-if="isOpen" class="item-history-shell fixed inset-0 z-50 overflow-y-auto" aria-labelledby="item-history-modal"
    role="dialog" aria-modal="true">
    <!-- Backdrop -->
    <div class="item-history-backdrop fixed inset-0 transition-opacity" @click="$emit('close')"></div>

    <!-- Modal panel -->
    <div class="flex items-center justify-center min-h-screen p-4">
      <div class="item-history-panel relative w-full max-w-3xl max-h-[88vh] flex flex-col">

        <!-- Header -->
        <div class="item-history-header flex items-center justify-between px-6 py-4">
          <div>
            <h3 class="item-history-title">{{ itemName }}</h3>
            <p class="item-history-subtitle">Sales, purchases &amp; profit breakdown</p>
          </div>
          <button type="button" @click="$emit('close')" class="item-history-close">
            <vue-feather type="x" size="18" />
          </button>
        </div>

        <!-- Metric cards (act as tab selectors) -->
        <div class="item-history-metrics">
          <!-- Sales card -->
          <button type="button" :class="['ihm-card ihm-card--green', activeTab === 'sales' && 'ihm-card--active']"
            @click="activeTab = 'sales'">
            <div class="ihm-card__header">
              <span class="ihm-card__icon ihm-card__icon--green">
                <vue-feather type="trending-up" size="14" />
              </span>
              <span class="ihm-card__label">Sales</span>
            </div>
            <span class="ihm-card__value">{{ formatPrice(totalSalesRevenue) }}</span>
            <span class="ihm-card__sub">{{ salesTotal }} transaction{{ salesTotal !== 1 ? 's' : '' }}</span>
          </button>

          <!-- Purchases card (products only) -->
          <button v-if="itemType === 'product'" type="button"
            :class="['ihm-card ihm-card--blue', activeTab === 'purchases' && 'ihm-card--active']"
            @click="activeTab = 'purchases'">
            <div class="ihm-card__header">
              <span class="ihm-card__icon ihm-card__icon--blue">
                <vue-feather type="package" size="14" />
              </span>
              <span class="ihm-card__label">Purchases</span>
            </div>
            <span class="ihm-card__value">{{ formatPrice(totalPurchaseCost) }}</span>
            <span class="ihm-card__sub">{{ purchasesTotal }} entr{{ purchasesTotal !== 1 ? 'ies' : 'y' }}</span>
          </button>

          <!-- Gross Profit card -->
          <button type="button"
            :class="['ihm-card', grossProfit >= 0 ? 'ihm-card--emerald' : 'ihm-card--red', activeTab === 'profit' && 'ihm-card--active']"
            @click="activeTab = 'profit'">
            <div class="ihm-card__header">
              <span class="ihm-card__icon"
                :class="grossProfit >= 0 ? 'ihm-card__icon--emerald' : 'ihm-card__icon--red'">
                <vue-feather type="bar-chart-2" size="14" />
              </span>
              <span class="ihm-card__label">Gross Profit</span>
            </div>
            <span class="ihm-card__value" :class="grossProfit >= 0 ? 'ihm-card__value--pos' : 'ihm-card__value--neg'">
              {{ formatPrice(grossProfit) }}
            </span>
            <span class="ihm-card__sub">{{ grossProfitMargin }}% margin</span>
          </button>
        </div>

        <!-- Detail content -->
        <div class="item-history-content flex-1 overflow-y-auto px-4 py-5">

          <!-- Loading -->
          <div v-if="isLoading" class="flex items-center justify-center py-14">
            <span
              class="inline-block h-7 w-7 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></span>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="text-center py-12">
            <p class="text-sm text-red-500 dark:text-red-400">Failed to load data. Please try again.</p>
          </div>

          <!-- Sales table -->
          <div v-else-if="activeTab === 'sales'">
            <div v-if="sales.length === 0" class="ihm-empty">
              <vue-feather type="shopping-bag" size="28" class="ihm-empty__icon" />
              <p>No sales recorded yet.</p>
            </div>
            <div v-else class="overflow-x-auto ihm-table-wrap">
              <table class="ihm-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Unit Price</th>
                    <th class="text-right">Total</th>
                    <th>Status</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="sale in sales" :key="sale.id">
                    <td class="whitespace-nowrap">{{ formatDate(sale.created_at) }}</td>
                    <td class="text-right whitespace-nowrap font-medium">
                      {{ formatQuantity(sale.quantity) }}
                      <span class="ihm-unit">{{ sale.product?.quantity_unit || '' }}</span>
                    </td>
                    <td class="text-right whitespace-nowrap">{{ formatPrice(sale.unit_price, sale.currency) }}</td>
                    <td class="text-right whitespace-nowrap font-semibold" style="color:#059669">
                      {{ formatPrice(sale.total_amount, sale.currency) }}
                    </td>
                    <td>
                      <span v-if="sale.sale_cart?.status"
                        :class="['ihm-badge', sale.sale_cart.status === 'PAID' ? 'ihm-badge--green' : 'ihm-badge--gray']">{{
                          sale.sale_cart.status }}</span>
                      <span v-else class="ihm-dash">—</span>
                    </td>
                    <td class="whitespace-nowrap">{{ sale.sale_cart?.recorded_by?.names || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Purchases table -->
          <div v-else-if="activeTab === 'purchases'">
            <div v-if="purchases.length === 0" class="ihm-empty">
              <vue-feather type="package" size="28" class="ihm-empty__icon" />
              <p>No purchases recorded yet.</p>
            </div>
            <div v-else class="overflow-x-auto ihm-table-wrap">
              <table class="ihm-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th class="text-right">Qty</th>
                    <th class="text-right">Unit Price</th>
                    <th class="text-right">Total</th>
                    <th>Expiry</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="purchase in purchases" :key="purchase.id">
                    <td class="whitespace-nowrap">{{ formatDate(purchase.created_at) }}</td>
                    <td class="text-right whitespace-nowrap font-medium">
                      {{ formatQuantity(purchase.quantity) }}
                      <span class="ihm-unit">{{ purchase.product?.quantity_unit || '' }}</span>
                    </td>
                    <td class="text-right whitespace-nowrap">{{ formatPrice(purchase.unit_price, purchase.currency) }}
                    </td>
                    <td class="text-right whitespace-nowrap font-semibold" style="color:var(--hs-primary,#3293ec)">
                      {{ formatPrice(purchase.total_amount, purchase.currency) }}
                    </td>
                    <td>
                      <span v-if="purchase.expiration_date"
                        :class="['ihm-badge', isExpired(purchase.expiration_date) ? 'ihm-badge--red' : 'ihm-badge--gray']">{{
                          formatDate(purchase.expiration_date) }}</span>
                      <span v-else class="ihm-dash">—</span>
                    </td>
                    <td class="whitespace-nowrap">{{ purchase.recorded_by?.names || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Gross Profit breakdown -->
          <div v-else-if="activeTab === 'profit'">
            <div v-if="totalSalesRevenue === 0" class="ihm-empty">
              <vue-feather type="bar-chart-2" size="28" class="ihm-empty__icon" />
              <p>No sales data to compute profit yet.</p>
            </div>
            <div v-else>
              <!-- P&L strip -->
              <div class="profit-strip">
                <div class="profit-strip__row">
                  <span>Revenue</span>
                  <span class="profit-strip__amount profit-strip__amount--pos">{{ formatPrice(totalSalesRevenue)
                    }}</span>
                </div>
                <div class="profit-strip__row">
                  <span>{{ hasTrackedCostOfGoodsSold ? 'Cost of Goods Sold' : 'Est. Cost of Goods Sold' }}</span>
                  <span class="profit-strip__amount profit-strip__amount--neg">− {{ formatPrice(costOfGoodsSold)
                    }}</span>
                </div>
                <div class="profit-strip__divider"></div>
                <div class="profit-strip__row profit-strip__row--total">
                  <span>Gross Profit</span>
                  <span class="profit-strip__amount"
                    :class="grossProfit >= 0 ? 'profit-strip__amount--pos' : 'profit-strip__amount--neg'">
                    {{ formatPrice(grossProfit) }}
                  </span>
                </div>
                <div class="profit-strip__row">
                  <span>Profit Margin</span>
                  <span class="profit-strip__amount"
                    :class="grossProfit >= 0 ? 'profit-strip__amount--pos' : 'profit-strip__amount--neg'">
                    {{ grossProfitMargin }}%
                  </span>
                </div>
              </div>

              <!-- Metrics grid -->
              <div class="profit-metrics">
                <div class="profit-metric">
                  <span class="profit-metric__label">Qty Sold</span>
                  <strong class="profit-metric__value">{{ formatQuantity(totalQuantitySold) }}</strong>
                </div>
                <div v-if="itemType === 'product'" class="profit-metric">
                  <span class="profit-metric__label">Qty Purchased</span>
                  <strong class="profit-metric__value">{{ formatQuantity(totalQuantityPurchased) }}</strong>
                </div>
                <div class="profit-metric">
                  <span class="profit-metric__label">Avg. Sale Price</span>
                  <strong class="profit-metric__value">
                    {{ totalQuantitySold > 0 ? formatPrice(totalSalesRevenue / totalQuantitySold) : '—' }}
                  </strong>
                </div>
                <div v-if="itemType === 'product'" class="profit-metric">
                  <span class="profit-metric__label">Avg. Cost Price</span>
                  <strong class="profit-metric__value">
                    {{ totalQuantityPurchased > 0 ? formatPrice(totalPurchaseCost / totalQuantityPurchased) : '—' }}
                  </strong>
                </div>
              </div>

              <p v-if="!hasTrackedCostOfGoodsSold" class="profit-cogs-note">
                <vue-feather type="info" size="12" />
                COGS is estimated based on average purchase price × quantity sold.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import {
  useProductItemHistory,
  useServiceItemHistory,
} from "@/composables/useItemHistoryQuery";

export default {
  name: "ItemHistoryModal",
  props: {
    isOpen: { type: Boolean, default: false },
    itemId: { type: [Number, String], default: null },
    itemType: { type: String, default: "product" },
    itemName: { type: String, default: "" },
  },
  emits: ["close"],
  setup(props) {
    const activeTab = ref("sales");

    const enabled = computed(() => props.isOpen && !!props.itemId);

    const productQuery = useProductItemHistory(
      computed(() => (props.itemType === "product" ? props.itemId : null)),
      { enabled: computed(() => enabled.value && props.itemType === "product") },
    );

    const serviceQuery = useServiceItemHistory(
      computed(() => (props.itemType === "service" ? props.itemId : null)),
      { enabled: computed(() => enabled.value && props.itemType === "service") },
    );

    const historyData = computed(() =>
      props.itemType === "product"
        ? productQuery.data?.value
        : serviceQuery.data?.value,
    );

    const isLoading = computed(() =>
      props.itemType === "product"
        ? productQuery.isLoading?.value
        : serviceQuery.isLoading?.value,
    );

    const error = computed(() =>
      props.itemType === "product"
        ? productQuery.error?.value
        : serviceQuery.error?.value,
    );

    const sales = computed(() => historyData.value?.sales || []);
    const purchases = computed(() => historyData.value?.purchases || []);
    const salesTotal = computed(() => historyData.value?.salesTotal ?? sales.value.length);
    const purchasesTotal = computed(() => historyData.value?.purchasesTotal ?? purchases.value.length);

    const totalSalesRevenue = computed(() => sales.value.reduce((s, x) => s + (x.total_amount || 0), 0));
    const totalQuantitySold = computed(() => sales.value.reduce((s, x) => s + (x.quantity || 0), 0));
    const totalPurchaseCost = computed(() => purchases.value.reduce((s, x) => s + (x.total_amount || 0), 0));
    const totalQuantityPurchased = computed(() => purchases.value.reduce((s, x) => s + (x.quantity || 0), 0));

    const trackedCOGS = computed(() => historyData.value?.costOfGoodsSold ?? null);
    const hasTrackedCostOfGoodsSold = computed(() => trackedCOGS.value != null);
    const estimatedCOGS = computed(() => {
      if (totalQuantityPurchased.value <= 0 || totalQuantitySold.value <= 0) return 0;
      return (totalPurchaseCost.value / totalQuantityPurchased.value) * totalQuantitySold.value;
    });
    const costOfGoodsSold = computed(() =>
      hasTrackedCostOfGoodsSold.value ? trackedCOGS.value : estimatedCOGS.value,
    );
    const grossProfit = computed(() => totalSalesRevenue.value - costOfGoodsSold.value);
    const grossProfitMargin = computed(() => {
      if (totalSalesRevenue.value <= 0) return 0;
      return Math.round((grossProfit.value / totalSalesRevenue.value) * 100);
    });

    watch(
      () => props.isOpen,
      (val) => { if (val) activeTab.value = "sales"; },
    );

    // If switched to service while on purchases tab, reset
    watch(
      () => props.itemType,
      (type) => { if (type === "service" && activeTab.value === "purchases") activeTab.value = "sales"; },
    );

    const formatPrice = (amount, currency = "RWF") => {
      if (amount == null) return "—";
      return `${Number(amount).toLocaleString()} ${currency || ""}`.trim();
    };

    const formatQuantity = (qty) => {
      if (qty == null) return "0";
      return Number(qty) % 1 === 0 ? Number(qty).toString() : Number(qty).toFixed(2);
    };

    const isExpired = (dateStr) => {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    };

    return {
      activeTab,
      isLoading,
      error,
      sales,
      salesTotal,
      purchases,
      purchasesTotal,
      totalSalesRevenue,
      totalQuantitySold,
      totalPurchaseCost,
      totalQuantityPurchased,
      hasTrackedCostOfGoodsSold,
      costOfGoodsSold,
      grossProfit,
      grossProfitMargin,
      formatPrice,
      formatQuantity,
      formatDate,
      isExpired,
    };
  },
};
</script>

<style scoped>
/* ── Shell & backdrop ──────────────────────────────────────── */
.item-history-backdrop {
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(6px);
}

/* ── Panel ─────────────────────────────────────────────────── */
.item-history-panel {
  background:
    radial-gradient(circle at 90% 0%, color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent), transparent 30%),
    var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
  overflow: hidden;
}

/* ── Header ────────────────────────────────────────────────── */
.item-history-header {
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--hs-primary, #3293ec) 8%, transparent), transparent),
    color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 72%, var(--hs-surface, #ffffff));
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
}

.item-history-title {
  color: var(--hs-text, #0f172a);
  font-size: 1.1rem;
  font-weight: 900;
}

.item-history-subtitle {
  color: var(--hs-muted, #64748b);
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 2px;
}

.item-history-close {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 9%, transparent);
  border-radius: 12px;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  transition: background 0.18s ease, transform 0.18s ease;
  width: 36px;
}

.item-history-close:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 10%, transparent);
  transform: translateY(-1px);
}

/* ── Metric cards ──────────────────────────────────────────── */
.item-history-metrics {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.18));
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  padding: 14px 20px;
}

.ihm-card {
  align-items: flex-start;
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 55%, var(--hs-surface, #ffffff));
  border: 1.5px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ihm-card:hover {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
  transform: translateY(-1px);
}

.ihm-card--active {
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 14%, transparent),
    0 6px 18px rgba(15, 23, 42, 0.08);
}

.ihm-card--green.ihm-card--active {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15), 0 6px 18px rgba(15, 23, 42, 0.08);
}

.ihm-card--blue.ihm-card--active {
  border-color: var(--hs-primary, #3293ec);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--hs-primary, #3293ec) 15%, transparent), 0 6px 18px rgba(15, 23, 42, 0.08);
}

.ihm-card--emerald.ihm-card--active {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15), 0 6px 18px rgba(15, 23, 42, 0.08);
}

.ihm-card--red.ihm-card--active {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15), 0 6px 18px rgba(15, 23, 42, 0.08);
}

.ihm-card__header {
  align-items: center;
  display: flex;
  gap: 7px;
}

.ihm-card__icon {
  align-items: center;
  border-radius: 9px;
  display: inline-flex;
  flex-shrink: 0;
  height: 28px;
  justify-content: center;
  width: 28px;
}

.ihm-card__icon--green {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.ihm-card__icon--blue {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  color: var(--hs-primary, #3293ec);
}

.ihm-card__icon--emerald {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.ihm-card__icon--red {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.ihm-card__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.ihm-card__value {
  color: var(--hs-text, #0f172a);
  font-size: 0.95rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ihm-card__value--pos {
  color: #059669;
}

.ihm-card__value--neg {
  color: #dc2626;
}

.ihm-card__sub {
  color: var(--hs-muted, #64748b);
  font-size: 0.71rem;
  font-weight: 500;
}

/* ── Data table ────────────────────────────────────────────── */
.ihm-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.ihm-table-wrap {
  margin-right: 0;
  padding-right: 0;
}

.ihm-table thead tr {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
}

.ihm-table th {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  padding: 10px 12px;
  text-align: left;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
}

.ihm-table tbody tr {
  border-bottom: 1px solid color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 55%, transparent);
  font-size: 0.85rem;
  transition: background 0.14s ease;
}

.ihm-table tbody tr:last-child {
  border-bottom: none;
}

.ihm-table tbody tr:hover {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 4%, transparent);
}

.ihm-table td {
  color: var(--hs-text, #111827);
  padding: 10px 12px;
  vertical-align: middle;
}

.ihm-table th:first-child,
.ihm-table td:first-child {
  padding-left: 16px;
}

.ihm-table th:last-child,
.ihm-table td:last-child {
  padding-right: 8px;
}

.ihm-table th.text-right,
.ihm-table td.text-right {
  text-align: right;
}

.ihm-unit {
  color: var(--hs-muted, #64748b);
  font-size: 0.75rem;
  margin-left: 2px;
}

.ihm-badge {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 2px 8px;
}

.ihm-badge--green {
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

.ihm-badge--gray {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 10%, transparent);
  color: var(--hs-muted, #64748b);
}

.ihm-badge--red {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.dark .ihm-badge--green {
  background: rgba(16, 185, 129, 0.18);
  color: #6ee7b7;
}

.dark .ihm-badge--red {
  background: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
}

.ihm-dash {
  color: var(--hs-muted, #64748b);
}

/* ── Empty state ───────────────────────────────────────────── */
.ihm-empty {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 48px 0;
  text-align: center;
}

.ihm-empty__icon {
  color: var(--hs-muted, #94a3b8);
}

.ihm-empty p {
  color: var(--hs-muted, #64748b);
  font-size: 0.875rem;
}

/* ── Gross Profit tab ──────────────────────────────────────── */
.profit-strip {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 55%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 16px;
  margin-bottom: 14px;
  overflow: hidden;
}

.profit-strip__row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 11px 16px;
}

.profit-strip__row+.profit-strip__row {
  border-top: 1px solid color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 50%, transparent);
}

.profit-strip__row span:first-child {
  color: var(--hs-muted, #64748b);
  font-size: 0.875rem;
}

.profit-strip__divider {
  border-top: 2px solid var(--hs-border, rgba(148, 163, 184, 0.3));
}

.profit-strip__row--total {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 5%, transparent);
}

.profit-strip__row--total span:first-child {
  color: var(--hs-text, #0f172a);
  font-weight: 700;
}

.profit-strip__amount {
  font-size: 0.9rem;
  font-weight: 700;
}

.profit-strip__amount--pos {
  color: #059669;
}

.profit-strip__amount--neg {
  color: #dc2626;
}

.profit-metrics {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  margin-bottom: 14px;
}

.profit-metric {
  background: color-mix(in srgb, var(--hs-bg, #f9fafb) 55%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
}

.profit-metric__label {
  color: var(--hs-muted, #64748b);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.profit-metric__value {
  color: var(--hs-text, #0f172a);
  font-size: 1rem;
  font-weight: 800;
}

.profit-cogs-note {
  align-items: center;
  color: var(--hs-muted, #94a3b8);
  display: flex;
  font-size: 0.75rem;
  gap: 5px;
}

/* ── Responsive ────────────────────────────────────────────── */
@media (max-width: 600px) {
  .item-history-metrics {
    grid-template-columns: 1fr 1fr;
  }

  .item-history-panel {
    max-height: 94vh;
  }
}
</style>
